import { FloatingBackButton, RoundedButton, Spinner } from "@greeter/matter";
import {
  FetchCategory,
  FetchCategoryCategories,
  SaveCategory,
} from "@greeter/venue-api";
import { TitledInput } from "../TitledInput";
import { useActor } from "@xstate/react";
import {
  CategoryChange,
  categoryEditorMachine,
  provideApiForActor,
} from "./CategoryEditorMachine";
import { Subscription } from "xstate";

import css from "./CategoryEditor.module.scss";
import { IconInput } from "../IconInput";
import { useEffect, useRef } from "react";

export interface CategoryEditorApiRequirements {
  fetchCategoryCategories: FetchCategoryCategories;
  fetchCategory: FetchCategory;
  saveCategory: SaveCategory;
}

export type CategoryEditorProps = {
  categoryId?: string;
  categoryCategoryId?: string;
  onSaving?: () => Promise<void> | void;
  onSaved?: () => Promise<void> | void;
  onExit?: () => void;
  api: CategoryEditorApiRequirements;
};

export function CategoryEditor(props: CategoryEditorProps) {
  const [snap, send, m] = useActor(
    provideApiForActor(categoryEditorMachine, props.api),
    { input: { categoryId: props.categoryId } }
  );

  function change(c: CategoryChange) {
    send({ type: "change", data: c });
  }

  useEffect(() => {
    if (props.categoryCategoryId) {
      change({ type: "setCategoryCategory", data: props.categoryCategoryId });
    }
  }, [props.categoryCategoryId]);

  useEffect(() => {
    const subs: Array<Subscription> = [];

    if (props.onSaved) {
      subs.push(m.on("saved", props.onSaved));
    }

    if (props.onSaving) {
      subs.push(m.on("saving", props.onSaving));
    }

    return () => subs.forEach((s) => s.unsubscribe());
  });

  return (
    <div className={css.CategoryEditor}>
      <FloatingBackButton
        onClick={(fallback) => {
          if (props.onExit) {
            return props.onExit();
          }

          fallback();
        }}
      />
      <h3 style={{ margin: "3rem 1rem" }}>
        <b>
          <span style={{ color: "var(--gm-color-primary-highlight)" }}>
            <b>Kategori</b>
          </span>
        </b>
      </h3>
      <TitledInput title="Navn">
        <IconInput
          value={snap.context.changes.value.name}
          onChange={(ev) => {
            change({ type: "setName", data: ev.currentTarget.value });
          }}
        />
      </TitledInput>
      <TitledInput title="Overkategori">
        <div className={css.SelectContainer}>
          <select
            className={css.Select}
            onChange={(ev) => {
              change({
                type: "setCategoryCategory",
                data:
                  ev.currentTarget.value === "none"
                    ? undefined
                    : ev.currentTarget.value,
              });
            }}
          >
            <option
              value="none"
              selected={!snap.context.changes.value.categoryCategoryId}
            >
              Ingen
            </option>
            {snap.context.categoryCategories.map((cc) => (
              <option
                key={cc.id}
                value={cc.id}
                selected={
                  snap.context.changes.value.categoryCategoryId === cc.id
                }
              >
                {cc.name}
              </option>
            ))}
          </select>
        </div>
      </TitledInput>
      <div style={{ margin: "0 1rem" }}>
        <RoundedButton
          active
          onClick={() => send({ type: "save" })}
          activeClassName={css.ActionButton}
        >
          Gem
        </RoundedButton>
      </div>
      <Spinner
        reason={
          snap.matches({ api: { category: "fetching" } }) && "Henter kategori"
        }
      />
      <Spinner
        reason={
          snap.matches({ api: { category: "saving" } }) && "Gemmer kategori"
        }
      />
    </div>
  );
}
